// src/App.js - Fixed authentication flow
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { SpeedInsights } from '@vercel/speed-insights/react';
import Sidebar from './components/Sidebar';
import EventsPage from './pages/EventsPage';
// EventDetailsPage moved to archive - not used in production routing
import CreateEventPage from './pages/CreateEventPage';
import CreateAlertRulePage from './pages/CreateAlertRulePage';
import EventSettingsPage from './pages/EventSettingsPage';
import VendorsPage from './pages/VendorsPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';
import InvitePage from './pages/InvitePage';
import PublicChatPage from './pages/PublicChatPage';
import MessageComposer from './pages/MessageComposer';
import LandingPage from './pages/LandingPage';
import BQMCalendarPage from './pages/BQMCalendarPage';
import FinanceDashboardPage from './pages/FinanceDashboardPage';
import ClaudeDesignDashboard from './pages/ClaudeDesignDashboard';
import AdminPage from './pages/AdminPage';
// Preview pages moved to archive - uncomment to re-enable dev previews
// import PreviewPage from './archive/preview-pages/PreviewPage';
// import NewDashboardPreview from './archive/preview-pages/NewDashboardPreview';
// import NewDashboardPage from './archive/preview-pages/NewDashboardPage';
// import ClaudeDesignPreview from './archive/preview-pages/ClaudeDesignPreview';
// import WelcomePage from './archive/preview-pages/WelcomePage';
import { authService } from './services/api';
import { ToastProvider } from './components/Toast';
import { Toaster } from 'react-hot-toast';
import './styles/index.css';
import './styles/package-tokens.css';
// TODO: Remove when fully migrated - Re-enabled as fallback
import './App.css';

// Protected route component
const PrivateRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Public route component (redirects to events if already logged in)
const PublicRoute = ({ children }) => {
  const isAuthenticated = authService.isAuthenticated();

  if (isAuthenticated) {
    return <Navigate to="/events" replace />;
  }

  return children;
};


function App() {
  return (
    <ToastProvider>
      <Router>
        <div className="app">
        {/* Skip link for keyboard navigation */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        <Routes>
          {/* Landing Page - always visible to everyone */}
          <Route path="/" element={<LandingPage />} />

          {/* Keep /welcome as alias for landing page */}
          <Route path="/welcome" element={<LandingPage />} />

          <Route path="/login" element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          } />

          <Route path="/signup" element={
            <PublicRoute>
              <SignupPage />
            </PublicRoute>
          } />

          <Route path="/invite/:token" element={<InvitePage />} />

          {/* Public Chat Page - No auth required */}
          <Route path="/chat/:chatToken" element={<PublicChatPage />} />

          {/* Main Production Dashboard */}
          <Route path="/events" element={
            <PrivateRoute>
              <ClaudeDesignDashboard />
            </PrivateRoute>
          } />

          <Route path="/events/:eventId" element={
            <PrivateRoute>
              <ClaudeDesignDashboard />
            </PrivateRoute>
          } />

          <Route path="/events/:eventId/:tab" element={
            <PrivateRoute>
              <ClaudeDesignDashboard />
            </PrivateRoute>
          } />

          {/* Legacy routes - keep for backwards compatibility */}
          <Route path="/event/:eventId" element={
            <PrivateRoute>
              <ClaudeDesignDashboard />
            </PrivateRoute>
          } />

          <Route path="/event/:eventId/:tab" element={
            <PrivateRoute>
              <ClaudeDesignDashboard />
            </PrivateRoute>
          } />

          <Route path="/create-event" element={
            <PrivateRoute>
              <>
                <Sidebar />
                <div id="main-content" className="main-content">
                  <CreateEventPage />
                </div>
              </>
            </PrivateRoute>
          } />

          <Route path="/event/:eventId/create-alert-rule" element={
            <PrivateRoute>
              <>
                <Sidebar />
                <div id="main-content" className="main-content">
                  <CreateAlertRulePage />
                </div>
              </>
            </PrivateRoute>
          } />

          <Route path="/event/:eventId/settings" element={
            <PrivateRoute>
              <>
                <Sidebar />
                <div id="main-content" className="main-content">
                  <EventSettingsPage />
                </div>
              </>
            </PrivateRoute>
          } />

          <Route path="/event/:eventId/compose-message" element={
            <PrivateRoute>
              <MessageComposer />
            </PrivateRoute>
          } />

          <Route path="/vendors" element={
            <PrivateRoute>
              <>
                <Sidebar />
                <div id="main-content" className="main-content">
                  <VendorsPage />
                </div>
              </>
            </PrivateRoute>
          } />

          {/* J49 Demo Routes */}
          <Route path="/j49/calendar" element={
            <PrivateRoute>
              <BQMCalendarPage />
            </PrivateRoute>
          } />

          <Route path="/j49/finance" element={
            <PrivateRoute>
              <FinanceDashboardPage />
            </PrivateRoute>
          } />

          {/* Admin Dashboard */}
          <Route path="/admin" element={
            <PrivateRoute>
              <AdminPage />
            </PrivateRoute>
          } />

          {/* Catch all route - redirect to home if authenticated, login if not */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
      </Router>
      <SpeedInsights />
      {/* React Hot Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#333',
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            padding: '16px'
          },
          success: {
            iconTheme: {
              primary: 'var(--pkg-success)',
              secondary: '#fff'
            }
          },
          error: {
            iconTheme: {
              primary: 'var(--pkg-error)',
              secondary: '#fff'
            }
          }
        }}
      />
    </ToastProvider>
  );
}

export default App;